const SITE_URL = (process.env.NEXT_PUBLIC_SITE_URL || 'https://smartbathroom.ir').replace(/\/+$/, '');

/**
 * Returns a normalized, authoritative canonical URL for the site.
 * 
 * Rules applied:
 * 1. Handles falsy/undefined values gracefully by returning root SITE_URL.
 * 2. If an absolute URL on the same or external origin is passed, extracts pathname.
 * 3. Strips query parameters (?foo=bar) and hash fragments (#hash) to keep canonical clean.
 * 4. Normalizes Unicode (NFC) for reliable Persian characters.
 * 5. Collapses multiple consecutive slashes (e.g. //path///to -> /path/to).
 * 6. Strips trailing slash for all paths except root ('/').
 * 
 * @param {string} [path='/'] - Relative path or URL
 * @returns {string} - Absolute canonical URL
 */
export function getCanonicalUrl(path = '/') {
  if (!path || typeof path !== 'string') {
    return `${SITE_URL}/`;
  }

  let cleaned = path.trim();

  // If already a full URL, parse out the pathname
  if (/^https?:\/\//i.test(cleaned)) {
    try {
      const parsed = new URL(cleaned);
      cleaned = parsed.pathname;
    } catch {
      cleaned = cleaned.replace(/^https?:\/\/[^/]+/i, '');
    }
  }

  // Strip query string and fragment
  cleaned = cleaned.split('?')[0].split('#')[0].trim();

  if (!cleaned || cleaned === '/') {
    return `${SITE_URL}/`;
  }

  // Ensure leading slash and collapse redundant slashes
  let normalizedPath = cleaned.startsWith('/') ? cleaned : `/${cleaned}`;
  normalizedPath = normalizedPath.replace(/\/+/g, '/');

  // Strip trailing slash for non-root paths (Next.js default route style)
  if (normalizedPath.length > 1 && normalizedPath.endsWith('/')) {
    normalizedPath = normalizedPath.slice(0, -1);
  }

  // Unicode NFC normalization for Persian/Arabic text
  if (typeof normalizedPath.normalize === 'function') {
    normalizedPath = normalizedPath.normalize('NFC');
  }

  return `${SITE_URL}${normalizedPath}`;
}
